import React, { useCallback, useState } from 'react'
import {
  ControlledCheckbox,
  ControlledHiddenInput,
  ControlledSelect,
  ControlledTextField,
} from 'transfix-form/components'
import { Box, InputLabel, Typography } from 'transfix-ui/core'
import { IConnectionConfigEnvironment, IConnectionTypes } from 'types/graphqlTypes'
import { getErrorMessage } from 'apps/edi-tools/utils'
import { FtpConnectionConfigGrid } from '../../ConnectionConfigs/ConnectionConfigGrids/FtpConnectionConfigGrid'
import { CodeEditor } from '../../EdiSelfService/Editor/CodeEditor'
import { useInboundFileRouterConfigModalFormContext } from '../hooks/useInboundFileRouterConfigModalForm'

const CONNECTION_TYPE_OPTIONS = [
  {
    label: 'SFTP with Private Key',
    value: IConnectionTypes.SFTP_WITH_PRIVATE_KEY,
  },
  {
    label: 'SFTP with Password',
    value: IConnectionTypes.SFTP_WITH_PASSWORD,
  },
  {
    label: 'FTP',
    value: IConnectionTypes.FTP,
  },
]

const ENVIRONMENT_OPTIONS = [
  {
    label: 'Production',
    value: IConnectionConfigEnvironment.production,
  },
  {
    label: 'Staging',
    value: IConnectionConfigEnvironment.staging,
  },
  {
    label: 'Development',
    value: IConnectionConfigEnvironment.development,
  },
]

export const InboundFileRouterConfigModalForm = () => {
  const [connectionType, setConnectionType] = useState<IConnectionTypes>(
    IConnectionTypes.SFTP_WITH_PRIVATE_KEY
  )
  const [environment, setEnvironment] = useState<IConnectionConfigEnvironment>(
    IConnectionConfigEnvironment.staging
  )

  const { control, errors } = useInboundFileRouterConfigModalFormContext()

  const connectionChangeHandler = useCallback(({ target }) => {
    const val = target.value || {}

    setConnectionType(val)
  }, [])

  const environmentChangeHandler = useCallback(({ target }) => {
    const val = target.value || {}

    setEnvironment(val)
  }, [])

  const [shipperCodeJsonData, setShipperCodeJsonData] = useState<string>(
    JSON.stringify(JSON.parse(control?.defaultValuesRef?.current?.shipperCodeJson || '{}'), null, 2)
  )

  const shipperCodeJsonDataChangeHandler = (jsonData: string) => {
    try {
      const transformedData = JSON.stringify(JSON.parse(jsonData), null, 2)
      setShipperCodeJsonData(transformedData)
      control.setValue('shipperCodeJson', transformedData)
    } catch (e) {
      return false
    }
    return true
  }

  return (
    <form data-test='InboundFileRouterConfigModalForm'>
      <div className='mb4 mt2'>
        <ControlledHiddenInput name='id' control={control} />
        <ControlledSelect
          control={control}
          name='connectionType'
          defaultValue={connectionType}
          onChange={connectionChangeHandler}
          SelectProps={{
            id: 'connectionType',
            dataTest: 'connection-type-single-select',
            label: 'Connection Type',
            options: CONNECTION_TYPE_OPTIONS,
            value: connectionType,
            error: !!getErrorMessage(errors, 'connectionType'),
            validationMessage: getErrorMessage(errors, 'connectionType'),
            required: true,
          }}
        />
      </div>
      <Box mt={1} mb={1} ml={0.5}>
        <ControlledCheckbox
          name='disabled'
          control={control}
          defaultValue={false}
          CheckboxProps={{
            label: <Typography variant='subtitle2'>Disabled</Typography>,
            required: true,
          }}
        />
      </Box>
      <ControlledSelect
        control={control}
        name='environment'
        defaultValue={IConnectionConfigEnvironment.staging}
        onChange={environmentChangeHandler}
        SelectProps={{
          dataTest: 'environment-single-select',
          label: 'Environment',
          options: ENVIRONMENT_OPTIONS,
          value: environment,
          error: !!getErrorMessage(errors, 'environment'),
          validationMessage: getErrorMessage(errors, 'environment'),
          required: true,
        }}
      />
      <FtpConnectionConfigGrid
        control={control}
        errors={errors}
        includeFilenameGlob={false}
        fieldsRequired
      />
      <ControlledTextField
        control={control}
        name='tmsName'
        TextInputProps={{
          label: 'TMS Name',
          dataTest: 'tms-name-field',
          error: !!getErrorMessage(errors, 'tmsName'),
          validationMessage: getErrorMessage(errors, 'tmsName'),
          required: true,
        }}
      />
      <Box mt={1}>
        <ControlledHiddenInput name='shipperCodeJson' control={control} />
        <InputLabel>Shipper Code JSON</InputLabel>
        <CodeEditor
          height='30vh'
          width='auto'
          value={shipperCodeJsonData}
          language='json'
          onInputDataChange={shipperCodeJsonDataChangeHandler}
        />
      </Box>
    </form>
  )
}
